import api from './api';
import { StoreLicense } from '../types';
import { API_ENDPOINTS } from '../constants';

interface CreateStoreLicenseRequest {
  store_id: string;
  license_name: string;
  description?: string;
  license_type?: string;
  issue_date?: string;
  expiry_date?: string;
  license_number?: string;
  issued_by?: string;
}

interface UpdateStoreLicenseRequest extends Partial<CreateStoreLicenseRequest> {}

const storeLicenseService = {
  /**
   * Get licenses for a store
   */
  getLicensesByStore: async (storeId: string): Promise<StoreLicense[]> => {
    const response = await api.get<{ success: boolean; message: string; data: StoreLicense[] }>(
      API_ENDPOINTS.STORE_LICENSES.GET_BY_STORE(storeId)
    );
    return response.data.data;
  },

  /**
   * Get license by ID
   */
  getLicenseById: async (licenseId: string): Promise<StoreLicense> => {
    const response = await api.get<{ success: boolean; message: string; data: StoreLicense }>(
      API_ENDPOINTS.STORE_LICENSES.GET_BY_ID(licenseId)
    );
    return response.data.data;
  },

  /**
   * Create license with documents
   */
  createLicense: async (licenseData: CreateStoreLicenseRequest, files: File[]): Promise<StoreLicense> => {
    const formData = new FormData();
    
    // Add license data
    Object.keys(licenseData).forEach((key) => {
      const value = licenseData[key as keyof CreateStoreLicenseRequest];
      if (value !== undefined && value !== null && value !== '') {
        formData.append(key, value.toString());
      }
    });

    // Add files
    files.forEach((file) => {
      formData.append('documents', file);
    });

    const response = await api.post<{ success: boolean; message: string; data: StoreLicense }>(
      API_ENDPOINTS.STORE_LICENSES.CREATE,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  },

  /**
   * Update license
   */
  updateLicense: async (licenseId: string, licenseData: UpdateStoreLicenseRequest): Promise<StoreLicense> => {
    const response = await api.put<{ success: boolean; message: string; data: StoreLicense }>(
      API_ENDPOINTS.STORE_LICENSES.UPDATE(licenseId),
      licenseData
    );
    return response.data.data;
  },

  /**
   * Delete license
   */
  deleteLicense: async (licenseId: string): Promise<void> => {
    await api.delete(API_ENDPOINTS.STORE_LICENSES.DELETE(licenseId));
  },

  /**
   * Add documents to license
   */
  addDocuments: async (licenseId: string, files: File[]): Promise<StoreLicense> => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('documents', file);
    });

    const response = await api.post<{ success: boolean; message: string; data: StoreLicense }>(
      API_ENDPOINTS.STORE_LICENSES.ADD_DOCUMENTS(licenseId),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  },

  /**
   * Delete document
   */
  deleteDocument: async (documentId: string): Promise<void> => {
    await api.delete(API_ENDPOINTS.STORE_LICENSES.DELETE_DOCUMENT(documentId));
  },
};

export default storeLicenseService;

